interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{
    document: string;
    similarity: number;
    chunk_id: string;
    preview: string;
  }>;
}

interface Conversation {
  chat_id: string;
  messages: Message[];
  created_at?: string;
  last_modified?: string;
  customer_slug?: string | null;
  workspace_slug?: string | null;
  agent_slug?: string | null;
  origin_mode?: 'voice' | 'text' | null;
  switched_to_text?: boolean;
  switched_at_index?: number | null;
}

export interface ConversationSummary {
  chat_id: string;
  message_count: number;
  last_modified: string;
  created_at: string;
  preview: string;
  has_feedback?: boolean;
  customer_slug?: string | null;
  workspace_slug?: string | null;
  agent_slug?: string | null;
  user_id?: string | null;
  username?: string | null;
  user_agent?: string | null;
}

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

const getAdminAuthHeaders = (): HeadersInit | undefined => {
  const token = window.sessionStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

class ConversationService {
  private static instance: ConversationService;

  private constructor() {}

  static getInstance(): ConversationService {
    if (!ConversationService.instance) {
      ConversationService.instance = new ConversationService();
    }
    return ConversationService.instance;
  }

  async getConversation(
    uuid: string,
    scope?: { customerSlug?: string | null; workspaceSlug?: string | null; agentSlug?: string | null },
  ): Promise<Conversation> {
    const params = new URLSearchParams();
    if (scope?.customerSlug) params.set('customer', scope.customerSlug);
    if (scope?.workspaceSlug) params.set('workspace', scope.workspaceSlug);
    if (scope?.agentSlug) params.set('agent', scope.agentSlug);
    const queryString = params.toString();
    const res = await fetch(
      `${API_BASE}/api/conversations/${uuid}${queryString ? `?${queryString}` : ''}`,
      { headers: getAdminAuthHeaders() },
    );
    if (!res.ok) {
      throw new Error(`Conversation ${uuid} not found (${res.status})`);
    }
    return res.json();
  }

  async listConversations(): Promise<ConversationSummary[]> {
    const res = await fetch(`${API_BASE}/api/conversations`, {
      headers: getAdminAuthHeaders(),
    });
    if (!res.ok) {
      throw new Error('Failed to load conversations');
    }
    const data = await res.json();
    return data.conversations;
  }
}

export default ConversationService;
